import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useAppFilters, useQueryNavigation } from '@/hooks';
import { useParams } from 'react-router-dom';
import { useFetchSensorVisualAIMetaDataQuery } from '@/store/api/dataLayersApi';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';

import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { DIAGNOSTICS_DATE_FORMAT } from '@/const';
import type { RootState } from '@/store/store';
import { EvidencePlaylist } from './components/EvidencePlaylist';
import { useVisualAI } from '@/hooks/AssetsStudioPage/MainContent/VisualAI/useVisualAI';
import { useDispatch } from 'react-redux';
import { setVisualAIActiveItemIndex } from '@/store/slices/assetStudioSlice';
import { EvidenceVideoPlayer } from './components/EvidenceVideoPlayer';
import { GraphsContainer } from './GraphsContainer';
import { BaseTabs } from '@/components/uiKit/Tabs/BaseTabs';
import { BeltDefects } from '../BeltDefects/BeltDefects';
import { HealthStatusIndicator } from '@/components/shared/display/StateIndicator';
import { VISUAL_AI_INNER_TABS, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';

interface Component {
  component_id: string;
  component_name?: string;
  [key: string]: unknown;
}

interface SensorDataProps {
  asset: Record<string, unknown>;
  components: Component[];
  diagnostics: unknown[];
  isLoadingDiagnostics?: boolean;
}

/**
 * `NewVisualAI`
 *
 * A dynamic, tabbed dashboard view for Visual AI features within the Asset Studio page.
 * This component renders different sections—Belt Defects and Belt Misalignment—based on the selected tab.
 * It also adapts behavior based on asset type (fixed vs. mobile) and dynamically fetches visual AI metadata.
 *
 * ### Features:
 * - Displays tab content for Belt Defects and Belt Misalignment
 * - Supports fallback skeleton UI while loading
 * - Belt Misalignment uses the GraphsContainer component
 *
 * @param asset - The currently selected asset object from upstream context
 * @param components - Array of components belonging to the asset
 * @param diagnostics - Diagnostic records related to the asset
 *
 * @returns Rendered JSX view of tabbed Visual AI sections
 */
export const NewVisualAI = ({ asset, components, diagnostics, isLoadingDiagnostics }: SensorDataProps) => {
  const { siteId, id: assetId } = useParams();
  const {
    currentParams: { selectedPart = null },
  } = useQueryNavigation();
  const { isMobileAsset } = useActiveAssetsType();
  const { selectedComponent: selectedComponentId, setFilters } = useAppFilters();
  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);

  // Clear stale Visual AI navigation params from URL on mount
  useEffect(() => {
    setFilters({
      [FILTER_PARAMS.SELECTED_DIAGNOSTIC]: null,
      [FILTER_PARAMS.EVIDENCE_RESOURCE_ID]: null,
    });
  }, []);

  const {
    selectedEvidence,
    setSelectedEvidence,
    visualAIPlaylistItems,
    isLoading: isLoadingEvidences,
    isFetching: isFetchingEvidences,
    isFlotationAsset,
  } = useVisualAI({
    asset,
    components,
    diagnostics,
  });
  const dispatch = useDispatch();

  const asset_name = typeof asset?.asset_name === 'string' ? asset.asset_name : '';
  const asset_model = typeof asset?.asset_model === 'string' ? asset.asset_model : '';
  const asset_supplier = typeof asset?.asset_supplier === 'string' ? asset.asset_supplier : '';

  // Determine if metadata query should be skipped based on asset completeness and type
  const skipQuery = useMemo(() => {
    // Skip if essential params are missing
    if (!siteId || !assetId) return true;
    // For mobile assets, wait for all required asset data
    if (isMobileAsset) {
      return !asset_name || !asset_model || !asset_supplier;
    }
    // For fixed assets, only need asset_name
    return !asset_name;
  }, [siteId, assetId, isMobileAsset, asset_name, asset_model, asset_supplier]);

  const queryParams = useMemo(() => {
    const params = {
      site_id: siteId!,
      asset_id: assetId!,
      component_id: selectedComponentId,
      subcomponent_id: !selectedPart || selectedPart.includes('custom-part-analysis') ? null : selectedPart,
      asset_category: isMobileAsset ? 'mobile' : 'fixed',
      asset_name,
      asset_model,
      asset_supplier,
      from_date: from ? format(new Date(from), DIAGNOSTICS_DATE_FORMAT) : null,
      to_date: to ? format(new Date(to), DIAGNOSTICS_DATE_FORMAT) : null,
    };
    return params;
  }, [
    siteId,
    assetId,
    selectedComponentId,
    selectedPart,
    isMobileAsset,
    asset_name,
    asset_model,
    asset_supplier,
    from,
    to,
  ]);

  const {
    data: config,
    isLoading,
    refetch,
    isFetching,
  } = useFetchSensorVisualAIMetaDataQuery(queryParams, {
    skip: skipQuery,
    refetchOnMountOrArgChange: true, // Force refetch when args change
  });

  // Only use config when not fetching to avoid showing stale data during component switches.
  // RTK Query's isFetching/isLoading flags already handle staleness since queryParams includes selectedComponentId.
  const displayConfig = useMemo(() => {
    if (isFetching || isLoading) return undefined;
    return config;
  }, [config, isFetching, isLoading]);

  // Get the first available tab section from the config
  // Handles beltMisalignment, rockSize, componentEvidence, and any future tab types
  const sectionType = useMemo(() => {
    if (!displayConfig?.tabs) return undefined;
    const tabKeys = Object.keys(displayConfig.tabs);
    console.log('%c[NewVisualAI] displayConfig.tabs keys:', 'color: #00bcd4; font-weight: bold;', tabKeys, {
      displayConfig,
      selectedComponentId,
      isFetching,
      isLoading,
    });
    if (tabKeys.length === 0) return undefined;
    return (displayConfig.tabs as Record<string, any>)[tabKeys[0]];
  }, [displayConfig]);
  const visualAIDataLayers = sectionType?.dataLayers ?? [];

  const [activeDataLayers, setActiveDataLayers] = useState<string[]>([]);
  const prevComponentRef = useRef<string | undefined>(undefined);
  const prevPartRef = useRef<string | null>(null);

  // Tab state for Belt components - always default to Belt Defects
  // Tab is managed locally (not via URL) since BaseTabs uses updateUrl={false}
  const [currentTab, setCurrentTab] = useState<string>(VISUAL_AI_INNER_TABS.BELT_DEFECTS);
  const previousTabRef = useRef<string | null>(null);

  // Determine if the selected component is a Belt
  const selectedComponent = useMemo(() => {
    if (!Array.isArray(components)) return undefined;
    return components.find((c) => c.component_id === selectedComponentId);
  }, [components, selectedComponentId]);

  const isBeltComponent = useMemo(() => {
    const componentName = selectedComponent?.component_name as string;
    return componentName?.toLowerCase()?.includes('belt');
  }, [selectedComponent]);

  // Detect component/part changes and force metadata refetch
  useEffect(() => {
    const hasComponentChanged =
      prevComponentRef.current !== undefined && prevComponentRef.current !== selectedComponentId;
    const hasPartChanged = prevPartRef.current !== null && prevPartRef.current !== selectedPart;

    if (hasComponentChanged || hasPartChanged) {
      // RTK Query will automatically abort the previous request when refetch is called
      // Only refetch if the query has been started (not skipped and either has data or is loading)
      if (refetch && !skipQuery && (config || isLoading || isFetching)) {
        refetch();
      }
    }

    prevComponentRef.current = selectedComponentId;
    prevPartRef.current = selectedPart;
  }, [selectedComponentId, selectedPart, refetch]);

  // Initialize default enabled data layers from the config
  useEffect(() => {
    if (visualAIDataLayers.length > 0) {
      const enabled = visualAIDataLayers.filter((el: any) => el.enabled).map((el: any) => el.id);
      setActiveDataLayers(enabled);
    }
  }, [visualAIDataLayers]);

  // Render loading skeleton
  const renderLoadingSkeleton = () => {
    if (isBeltComponent) {
      // Loading state for belt components (with tabs)
      return (
        <div className="h-full flex flex-col overflow-hidden">
          {/* Tab skeleton */}
          <div className="rounded-lg h-[2.8rem] mb-2">
            <Skeleton className="w-full h-full" />
          </div>
          {/* Content skeleton matching BeltDefects layout */}
          <div className="flex flex-col gap-2 w-full h-[calc(100%-2.8rem)] overflow-hidden pt-2">
            {/* Top Section skeleton */}
            <div className="flex-1 min-h-0 overflow-hidden">
              <Skeleton className="w-full h-full" />
            </div>
            {/* Bottom Section skeleton */}
            <div className="flex-shrink-0 h-[200px]">
              <Skeleton className="w-full h-full" />
            </div>
          </div>
        </div>
      );
    } else {
      // Loading state for non-belt components (no tabs)
      return (
        <div className="flex flex-col justify-start items-center w-full gap-2 min-h-0 flex-1">
          <div className="w-full h-12">
            <Skeleton className="w-full h-full" />
          </div>
          <div className="w-full min-h-0 flex flex-col gap-2 flex-1">
            <div className="w-full flex-1">
              <Skeleton className="w-full h-full" />
            </div>
            <div className="w-full flex-1">
              <Skeleton className="w-full h-full" />
            </div>
            <div className="w-full flex-1">
              <Skeleton className="w-full h-full" />
            </div>
          </div>
        </div>
      );
    }
  };

  // Render loading skeleton if query is skipped, still loading, or config doesn't match current component
  // Note: Belt Defects tab uses mock data and doesn't need sectionType, so skip that check for it
  const isBeltDefectsTab = isBeltComponent && currentTab === VISUAL_AI_INNER_TABS.BELT_DEFECTS;
  const shouldShowLoading = isBeltDefectsTab
    ? false // Belt Defects always ready (uses mock data)
    : skipQuery ||
      isLoading ||
      isFetching ||
      !sectionType ||
      isLoadingEvidences ||
      isFetchingEvidences ||
      isLoadingDiagnostics;

  if (shouldShowLoading) {
    return renderLoadingSkeleton();
  }

  const renderBeltMisalignmentContent = () => {
    // Show player if a playlist item is selected, OR fallback to first playlist item
    const evidenceToPlay = selectedEvidence || visualAIPlaylistItems?.[0]?.uniqueId;
    const shouldShowEvidence = !!evidenceToPlay;
    return (
      <div className="flex h-full gap-4 relative overflow-y-auto no-scrollbar !mt-0">
        {sectionType &&
          (() => {
            return (
              <div className="flex flex-col h-full w-full gap-2">
                <div className="flex  h-full w-full gap-4">
                  <div className="flex flex-col h-full w-full gap-4 overflow-auto">
                    {shouldShowEvidence && (
                      <EvidenceVideoPlayer
                        resource_id={evidenceToPlay!.split('_')[0]}
                        onClose={() => setSelectedEvidence(null)}
                        className={isFlotationAsset ? 'h-full max-h-full min-h-full' : ''}
                      />
                    )}
                    <GraphsContainer
                      asset={asset}
                      components={components}
                      diagnostics={diagnostics}
                      config={sectionType}
                      activeOperationalModes={[]}
                      activeDataLayers={activeDataLayers}
                      isTabView={Boolean(shouldShowEvidence)}
                    />
                  </div>
                  <EvidencePlaylist
                    selectedEvidence={selectedEvidence}
                    onPlaylistItemClick={(id: string) => {
                      const clickedItemIndex = visualAIPlaylistItems.findIndex((el: any) => el.uniqueId === id);
                      setSelectedEvidence(visualAIPlaylistItems[clickedItemIndex]?.uniqueId);
                      dispatch(setVisualAIActiveItemIndex(clickedItemIndex));
                    }}
                  />
                </div>
              </div>
            );
          })()}
      </div>
    );
  };

  // Create tabs for belt components
  const visualAITabs = [
    {
      id: VISUAL_AI_INNER_TABS.BELT_MISALIGNMENT,
      title: (
        <div className="flex gap-2">
          <span>Belt Misalignment</span>
          <HealthStatusIndicator type={'healthy'} />
        </div>
      ),
      disabled: false,
      element: renderBeltMisalignmentContent(),
    },
    {
      id: VISUAL_AI_INNER_TABS.BELT_DEFECTS,
      title: (
        <div className="flex gap-2">
          <span>Belt Defects</span>
          <HealthStatusIndicator type={'critical'} />
        </div>
      ),
      disabled: false,
      element: <BeltDefects />,
    },
  ];

  // For belt components, show tabs; for non-belt components, show content directly
  if (isBeltComponent) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <BaseTabs
          tabs={visualAITabs}
          defaultValue={VISUAL_AI_INNER_TABS.BELT_DEFECTS}
          variant="secondary-plain"
          updateUrl={false}
          onClick={(tabId: string) => {
            previousTabRef.current = tabId;
            setCurrentTab(tabId);
          }}
          classNames={{
            tabs: 'flex flex-col',
            tabList: { container: 'rounded-lg h-[2.8rem] flex-shrink-0' },
            tabContent: 'flex flex-col flex-1 min-h-0 gap-4 relative overflow-hidden !mt-0 pt-2',
          }}
        />
      </div>
    );
  }

  // For non-belt components (Rock Size, Crusher Unit), show the content directly without tabs
  return <div className="overflow-hidden h-full">{renderBeltMisalignmentContent()}</div>;
};
